'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetLeaderboardRow, useGetLeaderboard } from '@morfi/backend-sdk-v2/client';
import type { JSX } from 'react';
import { Podium } from '@/components/social/leaderboard/podium';
import { PodiumSkeleton } from '@/components/social/leaderboard/podium-skeleton';
import type { PodiumDataset, PodiumEntry, SocialTab } from '@/components/social/types';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { LB_FIELDS_BY_TAB } from '@/lib/sdk/projections';

// The podium is the top-3 of `useGetLeaderboard` for the active board. The
// `kind` discriminator uses `traders` where the tab is labelled `leaderboard`.
const TAB_TO_KIND: Record<SocialTab, 'traders' | 'boosters' | 'creators' | 'backers'> = {
  leaderboard: 'traders',
  boosters: 'boosters',
  creators: 'creators',
  backers: 'backers'
};
const PODIUM_LIMIT = 3;
interface SocialPodiumBlockProps {
  /** Active board — drives the top-3 `useGetLeaderboard` read and `<Podium tab=…>`. */
  tab: SocialTab;
  /** Active time window (`?window=`) — MUST match the list so the podium top-3
   *  equals the list's first 3 rows and patches off the same realtime subject. */
  timeWindow: string;
  /** Active sort (the list's wire `sortBy`) — same reason. */
  sortBy: string;
}

// Coerce the wire row into a `PodiumEntry`. The backend nulls every cross-board
// KPI not relevant to this card (the honest "not this card's metric"); default
// those to zero. Rank is the 1-based podium position — the rows arrive in
// rank order (SDK desc), so `index + 1` is the real top-3 standing.
function mapPodiumEntry(row: GetLeaderboardRow, index: number): PodiumEntry {
  const user = (row.user ?? {}) as Record<string, unknown>;
  return {
    user: {
      userId: typeof user.userId === 'string' ? user.userId : null,
      walletAddress: typeof user.walletAddress === 'string' ? user.walletAddress : '',
      displayName: typeof user.displayName === 'string' ? user.displayName : null,
      handle: typeof user.handle === 'string' ? user.handle : null,
      avatarUrl: typeof user.avatarUrl === 'string' ? user.avatarUrl : null,
      avatarNsfw: user.avatarNsfw === true,
      heroUrl: typeof user.heroUrl === 'string' ? user.heroUrl : null,
      heroNsfw: user.heroNsfw === true,
      connectedProviders: Array.isArray(user.connectedProviders) ? user.connectedProviders as PodiumEntry['user']['connectedProviders'] : []
    },
    rank: index + 1 as 1 | 2 | 3,
    totalPnl: String(row.totalPnl ?? '0'),
    totalVolume: String(row.totalVolume ?? '0'),
    totalTrades: Number(row.totalTrades ?? 0),
    totalAmountBoosted: String(row.totalAmountBoosted ?? '0'),
    numBoosts: Number(row.numBoosts ?? 0),
    marketsBoosted: Number(row.marketsBoosted ?? 0),
    topMarketVolume: String(row.topMarketVolume ?? '0'),
    totalMarketsCreated: Number(row.totalMarketsCreated ?? 0),
    creatorTotalVolume: String(row.creatorTotalVolume ?? '0'),
    totalBackedNetD6: String(row.totalBackedNetD6 ?? '0'),
    numBacks: Number(row.numBacks ?? 0)
  };
}

/**
 * Social-page podium — top-3 profile cards for the active tab.
 *
 * Rendered above the primary tab switcher in `SocialClient` so the segment
 * control sits under the cards on every tab.
 */
export function SocialPodiumBlock(t0) {
  const $ = _c(13);
  const {
    tab,
    timeWindow,
    sortBy
  } = t0;
  const t1 = TAB_TO_KIND[tab];
  const t2 = LB_FIELDS_BY_TAB[tab];
  let t3;
  if ($[0] !== t2) {
    t3 = [...t2];
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== sortBy || $[3] !== t1 || $[4] !== t3 || $[5] !== timeWindow) {
    t4 = {
      kind: t1,
      sortBy,
      sortOrder: "desc",
      window: timeWindow,
      fields: t3
    };
    $[2] = sortBy;
    $[3] = t1;
    $[4] = t3;
    $[5] = timeWindow;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const query = useGetLeaderboard(t4);
  const liveRows = useLiveListData(query.queryKey);
  const rows = liveRows.length > 0 ? liveRows : query.rows;
  let t5;
  if ($[7] !== rows) {
    t5 = rows.length > 0 ? rows.slice(0, PODIUM_LIMIT).map(mapPodiumEntry) : [];
    $[7] = rows;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const entries = t5;
  const isLoading = query.status === "pending" || query.isPlaceholderData;
  if (isLoading) {
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <PodiumSkeleton />;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  if (entries.length > 0) {
    let t6;
    if ($[10] !== entries || $[11] !== tab) {
      t6 = <Podium entries={entries} tab={tab} />;
      $[10] = entries;
      $[11] = tab;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  return null;
}